import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MeetingNoteTypes } from '../MeetingNoteTypes';
import { getFormLabelStyle } from '../../styles/designSystem';
import { createSafeCloseHandler } from '../../utils/modalUtils';
import { SmartAttendeeInput } from './SmartAttendeeInput';

interface AddMeetingNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
  projectUsers?: ProjectUserWithRole[];
  allUsers?: ProjectUserInterface[];
  token?: string;
}

interface ExternalUser {
  first_name: string;
  last_name: string;
  email?: string;
}

interface MeetingNoteFormData {
  notes: string;
  customers: string[]; // Array of user IDs from project team
  vendors: (string | ExternalUser)[]; // Array of user IDs or external user objects
  date: string;
  meeting_type_id: string;
}

export const AddMeetingNoteModal: React.FC<AddMeetingNoteModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
  projectUsers,
  allUsers,
  token
}) => {
  // const { keycloak: { idTokenParsed } } = useKeycloak();
  
  const [formData, setFormData] = useState<MeetingNoteFormData>({
    notes: '',
    customers: [],
    vendors: [],
    date: new Date().toISOString().split('T')[0],
    meeting_type_id: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [meetingTypeError, setMeetingTypeError] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomersChange = (attendees: (string | ExternalUser)[]) => {
    // Customers should only be strings (project team members)
    const customerIds = attendees.filter(a => typeof a === 'string') as string[];
    setFormData(prev => ({ ...prev, customers: customerIds }));
  };

  const handleVendorsChange = (attendees: (string | ExternalUser)[]) => {
    setFormData(prev => ({ ...prev, vendors: attendees }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }

    if (!formData.meeting_type_id) {
      setMeetingTypeError(true);
      toast.error('Meeting Type is required');
      return;
    }

    setIsLoading(true);
    try {
      const itemData = {
        project_id: projectData.id,
        notes: formData.notes,
        customers: formData.customers,
        vendors: formData.vendors,
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
        meeting_type_id: formData.meeting_type_id,
      };
      
      console.log('AddMeetingNoteModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      console.log('AddMeetingNoteModal: meeting note added successfully');
      toast.success('Meeting note added successfully');
      
      // Reset form
      setFormData({
        notes: '',
        customers: [],
        vendors: [],
        date: new Date().toISOString().split('T')[0],
        meeting_type_id: ''
      });
      
      onClose();
    } catch (error) {
      console.error('AddMeetingNoteModal: failed to add meeting note', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add meeting note';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['date'] // Exclude auto-filled fields
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center"
      style={{ zIndex: 1000000 }}
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 text-[#aaa] hover:text-[#555] p-2"
        style={{ zIndex: 1000001 }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div 
        className="bg-white rounded-xl border border-[#eaeaea] shadow-[0_4px_12px_rgba(0,0,0,0.08)] p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-[#171717]">Add Meeting Note</h2>
          <p className="text-sm text-[#888] mt-1">Create a new meeting note for this project</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date and Meeting Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full text-sm bg-white border border-[#eaeaea] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-0 focus:border-[#888]"
                required
              />
            </div>

            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Meeting Type <span className="text-red-500">*</span>
              </label>
              <MeetingNoteTypes
                onChange={(selectedOption: any) => {
                  const value = selectedOption?.value || '';
                  setFormData(prev => ({ ...prev, meeting_type_id: value }));
                  if (value) setMeetingTypeError(false);
                }}
                project_id={projectData.id || ''}
                defaultValue={formData.meeting_type_id}
                onCreate={(value: string) => {
                  setFormData(prev => ({ ...prev, meeting_type_id: value }));
                  setMeetingTypeError(false);
                }}
                token={token}
                hasError={meetingTypeError}
              />
            </div>
            </div>

          {/* Attendees */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Customer Attendees
              </label>
              <SmartAttendeeInput
                users={projectUsers || []}
                allUsers={allUsers}
                selectedAttendees={formData.customers as (string | ExternalUser)[]}
                onAttendeesChange={handleCustomersChange}
                placeholder="Type customer name..."
                maxAttendees={10}
                allowExternal={false}
              />
            </div>

            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Vendor Attendees
              </label>
              <SmartAttendeeInput
                users={projectUsers || []}
                allUsers={allUsers}
                selectedAttendees={formData.vendors}
                onAttendeesChange={handleVendorsChange}
                placeholder="Type vendor name..."
                maxAttendees={10}
                allowExternal={true}
              />
            </div>
          </div>

          {/* Meeting Notes */}
          <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Meeting Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={6}
                maxLength={12000}
              className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-0 focus:border-[#888] resize-none"
                placeholder="Enter meeting notes, discussions, decisions, and action items..."
              />
              <div
                className={`mt-1 text-right text-xs ${
                  formData.notes.length >= 12000 ? 'text-red-500' : 'text-[#888]'
                }`}
              >
                {formData.notes.length.toLocaleString()} / {(12000).toLocaleString()}
              </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#eaeaea]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-[#444] bg-[#f5f5f5] rounded-lg hover:bg-[#eaeaea] focus:outline-none focus:ring-0 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2.5 bg-[#171717] text-white rounded-lg hover:bg-[#333] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Adding...' : 'Add Meeting Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 